// ── CLASSES: admin catalog, roster, and preview ──────────────────────
// Catalog CRUD (list/tabs/modal/save/duplicate/archive) is now shared with
// Summer Workshops via catalog-shared.js -- tvMonthlyLabel/tvEligiblePrograms/
// tvIsFull are imported from there too, since they're also read outside the
// admin catalog (parent-classes.js's real registration flow,
// parent-dashboard.js's banner, and admin-settings.js).
//
// calcAge stays as a bare identifier: it's a top-level `export const` in
// core/format.js's classic-script window bridge, part of the shared global
// lexical environment every script on the page can already see, same as esc.
//
// Catalog's "+ Add offering" button wired via VIEW_INIT['a-tapvoice-catalog']
// + catalog-shared.js's wireCatalogGroup('tap_voice'). Roster's
// program-switch and promote buttons are wired by wireTapVoiceRoster(),
// called both from VIEW_INIT['a-tapvoice-roster'] and directly inside
// tvrSelectProgram() itself, which bypasses route()/VIEW_INIT the same way
// admin-workshops.js's wrSelectProgram() does (see its comment).
//
// View-only admins reach both tabs -- they need to see who's in each class --
// so every mutating control ("+ Add offering", "+ Add performer", Promote,
// Remove) is canEdit(session)-gated. Catalog's shared Edit/Duplicate/Archive
// row buttons are gated the same way inside catalog-shared.js.
import { apiFetch } from '../core/api.js';
import { DB, PROGRAMS, currentView, session } from '../core/state.js';
import { acctName, isArchived, canEdit } from '../core/domain-utils.js';
import { toast, toastError, openModal, closeModal } from '../core/ui.js';
import { VIEWS, VIEW_INIT, route, refresh, viewParent } from '../core/routing.js';
import { renderCatalogTabs, renderCatalogList, wireCatalogGroup, catalogModal, tvMonthlyLabel, tvEligiblePrograms, tvIsFull } from './catalog-shared.js';
import { PREVIEW_PERFORMERS } from './admin-store.js';
import { archivedPill, filterArchived, archivedShowSelect } from './admin-archive.js';

VIEWS['a-tapvoice-catalog'] = () => `
  <div class="page-head">
    <h2 class="serif"><em>Classes</em></h2>
  </div>
  <div class="card">
    <div class="card-hd">Catalog <span style="display:flex;gap:8px"><button class="btn-ghost btn-sm" onclick="route('a-classes-preview')">Preview</button>${canEdit(session) ? '<button class="btn-ghost btn-sm" data-action="add-offering">+ Add offering</button>' : ''}</span></div>
    ${renderCatalogTabs('tap_voice')}
    <div id="tvCatalogList">${renderCatalogList('tap_voice')}</div>
  </div>`;

VIEW_INIT['a-tapvoice-catalog'] = () => {
  document.querySelectorAll('[data-action="add-offering"]').forEach(btn => {
    btn.addEventListener('click', () => catalogModal('tap_voice'));
  });
  wireCatalogGroup('tap_voice');
};

// ── TAP & VOICE: admin roster ─────────────────────────────────────────
let tvrProgramId = null;
let tvrRoster = null;    // null = not loaded yet for tvrProgramId
let tvrLoadingId = null; // id currently being fetched, if any
let tvrShow = 'current';  // 'current' | 'archived' | 'all' -- see filterArchived()

// Same re-entrancy guard as wrSelectProgram() in admin-workshops.js — see
// its comment for why tvrLoadingId is needed.
async function tvrSelectProgram(id) {
  tvrProgramId = id;
  tvrRoster = null;
  tvrLoadingId = id;
  if (currentView === 'a-tapvoice-roster') { document.getElementById('main').innerHTML = VIEWS['a-tapvoice-roster'](); wireTapVoiceRoster(); }
  try {
    const data = await apiFetch('/workshops/' + id + '/roster');
    if (tvrProgramId !== id) return; // user switched offerings while this was in flight
    tvrRoster = data;
  } catch (e) {
    if (tvrProgramId !== id) return;
    tvrRoster = [];
    toastError(e.message);
  }
  tvrLoadingId = null;
  if (currentView === 'a-tapvoice-roster') { document.getElementById('main').innerHTML = VIEWS['a-tapvoice-roster'](); wireTapVoiceRoster(); }
}
function wireTapVoiceRoster() {
  document.querySelectorAll('[data-action="tvr-select-program"]').forEach(btn => {
    btn.addEventListener('click', () => tvrSelectProgram(btn.dataset.program));
  });
  const showSel = document.getElementById('tvrShowSelect');
  if (showSel) showSel.addEventListener('change', () => {
    tvrShow = showSel.value;
    document.getElementById('main').innerHTML = VIEWS['a-tapvoice-roster'](); wireTapVoiceRoster();
  });
  document.querySelectorAll('[data-action="tvr-promote"]').forEach(btn => {
    btn.addEventListener('click', () => promoteTVEnrollment(btn.dataset.enrollment));
  });
  document.querySelectorAll('[data-action="tvr-add"]').forEach(btn => {
    btn.addEventListener('click', tvrAddModal);
  });
  document.querySelectorAll('[data-action="tvr-remove"]').forEach(btn => {
    btn.addEventListener('click', () => tvrRemoveModal(btn.dataset.enrollment, btn.dataset.performer));
  });
}
VIEW_INIT['a-tapvoice-roster'] = wireTapVoiceRoster;
async function promoteTVEnrollment(enrollmentId) {
  try {
    const result = await apiFetch('/tap-voice/' + enrollmentId + '/promote', { method: 'POST', body: JSON.stringify({}) });
    toast(result.message || 'Performer promoted.');
    await tvrSelectProgram(tvrProgramId); // re-fetch and re-render this offering's roster
  } catch (e) { toastError(e.message); }
}

// Manually remove a performer who registered but won't be attending -- see
// TapVoiceRoutes::remove() for why this never forces a balance charge or
// cancellation fee, unlike PC/BB's dropModal()/processDrop() in
// admin-student-actions.js. Any already-posted charges are left as-is.
function tvrRemoveModal(enrollmentId, performerName) {
  openModal(`
    <h3>Remove from roster</h3>
    <p class="card-intro">Remove <b style="color:var(--cream)">${esc(performerName)}</b> from this class? This stops future monthly billing. Any charges already posted are left as-is -- adjust those separately if needed.</p>
    <div style="display:flex;gap:10px">
      <button class="btn" id="tvrRemoveBtn" style="flex:1;background:#f87171">Remove</button>
      <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
    </div>`, true);
  document.getElementById('tvrRemoveBtn').addEventListener('click', () => tvrRemoveConfirm(enrollmentId));
}
async function tvrRemoveConfirm(enrollmentId) {
  try {
    const result = await apiFetch('/tap-voice/' + enrollmentId + '/remove', { method: 'POST', body: JSON.stringify({}) });
    closeModal();
    toast(result.message || 'Performer removed.');
    await tvrSelectProgram(tvrProgramId);
  } catch (e) { toastError(e.message); }
}

// Manually reserve a spot for a performer (e.g. a returning Voice student)
// ahead of general registration opening -- see TapVoiceRoutes::adminEnroll()
// for why this always lands as 'registered' with no email, unlike the
// parent-facing registerPerformers()/promote() flows.
let tvrAddSelectedId = null;

// Whether *this* month's tuition is still collectable for an offering. Classes
// bill on the 1st from the Active-months checkboxes and monthly_billing.php
// never bills a month it didn't run in, so a performer added on the 9th would
// otherwise owe nothing for the month they actually start in. Mirrors the
// guards in TapVoiceRoutes::billCurrentMonth(), which is what actually decides.
// Spelled out rather than reusing catalog-shared.js's TV_MONTH_NAMES: those are
// the abbreviations for the month-picker chips, and this is a sentence.
const TVR_MONTH_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function tvrBillableMonth(program, seasonYear) {
  const month = TODAY.getMonth() + 1;
  if (!(program.monthly > 0) || !(program.seasonMonths || []).includes(month)) return null;
  // The server's season rule (BillingRun::seasonOf): only while today belongs to
  // this season, which runs September 1 to August 31.
  if (TODAY.getFullYear() - (TODAY.getMonth() >= 8 ? 0 : 1) !== seasonYear) return null;
  return { month, name: TVR_MONTH_FULL[month - 1], amount: program.monthly };
}
function tvrAddModal() {
  const offerings = PROGRAMS.filter(p => p.billingType === 'workshop' && p.workshopGroup === 'tap_voice' && p.active !== false);
  const current = offerings.find(p => p.id === tvrProgramId);
  if (!current) return;
  tvrAddSelectedId = null;
  const onRoster = new Set((tvrRoster || []).map(r => r.performerId));
  const seasonYear = DB.settings.upcomingSeasonYear;
  const billable = tvrBillableMonth(current, seasonYear);
  openModal(`
    <h3>Add performer to ${esc(current.name)}</h3>
    <p style="font-size:12px;color:var(--muted);margin-bottom:14px">Adds directly as registered — bypasses capacity/waitlist and sends no registration email. Use this to hold a spot before general registration opens.</p>
    <div class="field"><label>Performer</label>
      <input type="text" id="tvrAddSearch" placeholder="Search by performer name…" autocomplete="off">
    </div>
    <div id="tvrAddResults" style="max-height:220px;overflow-y:auto;margin-bottom:14px"></div>
    ${billable ? `
    <label style="display:flex;gap:8px;align-items:flex-start;margin-bottom:6px;cursor:pointer;text-transform:none;letter-spacing:0;font-weight:400">
      <input type="checkbox" id="tvrAddBillNow" checked style="width:auto;flex-shrink:0;margin-top:3px;accent-color:var(--gold)">
      <span style="font-size:13px;color:var(--cream)">Also bill ${billable.name} tuition now — ${fmt$(billable.amount)}
        <span style="display:block;font-size:11.5px;color:var(--muted);margin-top:3px;line-height:1.5">Charges the card on file now and emails the usual receipt. Monthly billing runs on the 1st and never bills a month it has already run, so without this they owe nothing for ${billable.name}.</span>
      </span>
    </label>` : ''}
    <div id="tvrAddCardWarn" style="font-size:11.5px;color:var(--red);margin-bottom:10px;line-height:1.5"></div>
    <button class="btn" id="tvrAddConfirmBtn" disabled>Add to roster</button>`);

  const renderResults = query => {
    const q = query.trim().toLowerCase();
    const resultsEl = document.getElementById('tvrAddResults');
    if (!q) { resultsEl.innerHTML = ''; return; }
    // An archived performer shouldn't be addable to a roster -- restore them
    // first if they're really coming back.
    const matches = DB.children.filter(k => !onRoster.has(k.id) && !isArchived(k) && k.name.toLowerCase().includes(q)).slice(0, 20);
    resultsEl.innerHTML = matches.length ? matches.map(k => `
      <div data-action="tvr-add-pick" data-performer="${k.id}" style="padding:8px 10px;border-radius:6px;cursor:pointer;font-size:13px;color:var(--cream);border:1px solid var(--border-soft);margin-bottom:4px">
        ${esc(k.name)} <span style="color:var(--muted);font-size:12px">(age ${calcAge(k.dob)}) — ${esc(acctName(k.accountId))}</span>
      </div>`).join('') : '<p class="empty" style="padding:0">No matches.</p>';
    resultsEl.querySelectorAll('[data-action="tvr-add-pick"]').forEach(el => {
      el.addEventListener('click', () => {
        tvrAddSelectedId = el.dataset.performer;
        resultsEl.querySelectorAll('[data-action="tvr-add-pick"]').forEach(x => x.style.borderColor = 'var(--border-soft)');
        el.style.borderColor = 'rgba(201,168,76,0.5)';
        // A class needs a card on file (the server refuses without one), so
        // say so here and keep Add disabled rather than letting it fail.
        const performer = DB.children.find(k => k.id === tvrAddSelectedId);
        const acct = performer ? DB.users.find(u => u.id === performer.accountId) : null;
        const noCard = !!(acct && !acct.card);
        document.getElementById('tvrAddConfirmBtn').disabled = noCard;
        const warnEl = document.getElementById('tvrAddCardWarn');
        if (warnEl) {
          warnEl.textContent = noCard
            ? 'No card on file for this family. Add one on their family page first: a class bills the card every month.'
            : '';
        }
      });
    });
  };
  document.getElementById('tvrAddSearch').addEventListener('input', e => renderResults(e.target.value));
  document.getElementById('tvrAddConfirmBtn').addEventListener('click', () => tvrAddConfirm(current.id));
}
async function tvrAddConfirm(programId) {
  if (!tvrAddSelectedId) return;
  try {
    const billNowEl = document.getElementById('tvrAddBillNow');
    const result = await apiFetch('/tap-voice/' + programId + '/enroll', {
      method: 'POST',
      body: JSON.stringify({
        performerId: tvrAddSelectedId,
        seasonYear: DB.settings.upcomingSeasonYear,
        billNow: !!(billNowEl && billNowEl.checked)
      })
    });
    closeModal();
    // A declined card is not a failed add -- the performer is on the roster and
    // the tuition is on the family's balance -- but it needs the louder toast.
    if (result.billed && !result.billed.ok) toastError(result.message);
    else toast(result.message || 'Performer added.');
    if (result.billed) DB.charges = await apiFetch('/charges');
    await tvrSelectProgram(tvrProgramId);
  } catch (e) { toastError(e.message); }
}
VIEWS['a-tapvoice-roster'] = () => {
  const offerings = PROGRAMS.filter(p => p.billingType === 'workshop' && p.workshopGroup === 'tap_voice' && p.active !== false);
  if (!offerings.length) {
    return `<div class="page-head"><h2 class="serif">Classes <em>Roster</em></h2></div>
      <p class="empty">No active Tap or Voice offerings — check the Catalog tab's Archived list if you're looking for a past one.</p>`;
  }
  if (!tvrProgramId || !offerings.some(p => p.id === tvrProgramId)) tvrProgramId = offerings[0].id;
  if (tvrRoster === null && tvrLoadingId !== tvrProgramId) tvrSelectProgram(tvrProgramId); // kicks off the fetch; re-renders #main when it resolves

  const current = offerings.find(p => p.id === tvrProgramId);
  const all = tvrRoster || [];
  const rows = filterArchived(all, tvrShow);
  const archivedCount = all.filter(r => r.archivedAt).length;
  return `
    <div class="page-head"><h2 class="serif">Classes <em>Roster</em></h2></div>
    <div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:18px">
      ${offerings.map(p => `<button class="btn-ghost btn-sm" style="${p.id === tvrProgramId ? 'border-color:rgba(201,168,76,0.5);color:var(--gold-light)' : ''}" data-action="tvr-select-program" data-program="${p.id}">${esc(p.name)}</button>`).join('')}
    </div>
    <div class="card">
      <div class="card-hd" style="display:flex;align-items:center;gap:10px">${esc(current ? current.name : '')} — ${rows.length} ${rows.length === 1 ? 'signup' : 'signups'} ${canEdit(session) ? '<button class="btn-ghost btn-sm" data-action="tvr-add">+ Add performer</button>' : ''}${archivedShowSelect('tvrShowSelect', tvrShow, archivedCount)}</div>
      ${tvrRoster === null ? '<p class="empty">Loading…</p>' : !rows.length ? `<p class="empty">${all.length ? 'No ' + (tvrShow === 'archived' ? 'archived' : 'current') + ' performers on this roster.' : 'No registrations yet.'}</p>` : `
      <div class="tbl-wrap"><table>
        <thead><tr><th>Performer</th><th>Age / Gender</th><th>Parent</th><th>Contact</th><th>Status</th><th>Payment</th><th></th></tr></thead>
        <tbody>
          ${rows.map(r => `<tr>
            <td>${r.accountId ? `<button class="tbl-link" onclick="viewParent('${r.accountId}')">${esc(r.performerName)}</button>` : esc(r.performerName)}${r.archivedAt ? ' ' + archivedPill() : ''}</td>
            <td style="color:var(--muted)">${r.age} · ${r.gender ? esc(r.gender) : '—'}</td>
            <td>${esc(r.parentName)}</td>
            <td style="color:var(--muted)">${esc(r.parentEmail)}${r.parentPhone ? '<br>' + esc(r.parentPhone) : ''}</td>
            <td>${r.status === 'waitlisted' ? '<span class="pill pill-off">waitlisted</span>' : '<span class="pill pill-off">registered</span>'}</td>
            <td>${r.paymentStatus === 'paid' ? 'Paid' : r.paymentStatus === 'n/a' ? '<span style="color:var(--muted)">Bills monthly</span>' : esc(r.paymentStatus)}</td>
            <td style="text-align:right;white-space:nowrap">
              ${canEdit(session) ? `
              ${r.status === 'waitlisted' ? `<button class="btn-ghost btn-sm" data-action="tvr-promote" data-enrollment="${r.enrollmentId}">Promote</button>` : ''}
              <button class="btn-ghost btn-sm" data-action="tvr-remove" data-enrollment="${r.enrollmentId}" data-performer="${esc(r.performerName)}">Remove</button>` : ''}
            </td>
          </tr>`).join('')}
        </tbody>
      </table></div>`}
    </div>`;
};

// ── CLASSES: admin preview ──────────────────────────────────────────────
// Same convention as the Store preview in admin-store.js: a fully separate,
// local-only simulation against PREVIEW_PERFORMERS (imported from
// admin-store.js), reusing the real eligibility/fill logic
// (tvEligiblePrograms/tvIsFull/tvMonthlyLabel) but never touching a real
// family or calling the real /tap-voice/register endpoint.
let classesPreviewSelections = {}; // performerId -> { programId: true }, local-only
function classesPreviewToggle(performerId, programId) {
  classesPreviewSelections[performerId] = classesPreviewSelections[performerId] || {};
  if (classesPreviewSelections[performerId][programId]) delete classesPreviewSelections[performerId][programId];
  else classesPreviewSelections[performerId][programId] = true;
  refresh();
}
function classesPreviewSubmit() {
  toast('Preview complete — nothing was saved or charged.');
}
VIEWS['a-classes-preview'] = () => {
  const kids = PREVIEW_PERFORMERS;
  const anySelected = kids.some(k => classesPreviewSelections[k.id] && Object.keys(classesPreviewSelections[k.id]).length);
  return `
    <div class="page-head">
      <span class="eyebrow" style="cursor:pointer" onclick="route('a-tapvoice-catalog')">‹ Classes</span>
      <h2 class="serif">Classes <em>Preview</em></h2>
    </div>
    <div style="background:rgba(201,168,76,0.08);border:1px solid rgba(201,168,76,0.3);border-radius:8px;padding:10px 14px;font-size:12px;color:var(--gold-light);margin-bottom:16px;max-width:640px">
      Preview mode — this is what a parent sees. Sample performers only; nothing here is saved, charged, or emailed.
    </div>
    <p style="font-size:13px;color:var(--muted);margin-bottom:16px;max-width:560px">Nothing is charged today. Classes bill monthly, same as Performance Company/Broadway Bound, to your card on file — see each class's active months below.</p>
    <div class="card" style="max-width:640px">
      ${kids.map(k => {
        const eligible = tvEligiblePrograms(k);
        if (!eligible.length) return '';
        const sel = classesPreviewSelections[k.id] || {};
        return `
        <div style="padding:16px 0;border-bottom:1px solid var(--border-soft)">
          <div style="font-size:15px;color:var(--cream);margin-bottom:8px">${esc(k.name)} <span style="font-size:12px;color:var(--muted)">(age ${calcAge(k.dob)})</span></div>
          ${eligible.map(p => {
            const full = tvIsFull(p);
            const checked = !!sel[p.id];
            const d = p.details || {};
            const metaParts = [d.dates, d.time, d.teacher].filter(Boolean);
            return `<div style="padding:7px 10px;border-radius:6px;border:1px solid ${checked ? 'rgba(201,168,76,0.4)' : 'var(--border-soft)'};background:${checked ? 'rgba(201,168,76,0.06)' : 'transparent'};margin-bottom:6px">
              <label style="display:flex;align-items:center;gap:8px;cursor:pointer">
                <input type="checkbox" ${checked ? 'checked' : ''} data-action="preview-toggle" data-kid="${k.id}" data-program="${p.id}" style="width:auto;accent-color:var(--gold)">
                <span style="flex:1;font-size:13px;color:var(--cream)">${esc(p.name)}${full ? ' <span style=\"color:var(--gold-light);font-size:11px\">(waitlist — full)</span>' : ''}</span>
                <span style="font-size:12px;color:var(--gold-light)">${tvMonthlyLabel(p)}</span>
              </label>
              ${metaParts.length ? `<div style="font-size:11px;color:var(--muted);margin-top:5px;padding-left:26px">${metaParts.map(esc).join(' · ')}</div>` : ''}
              ${d.description ? `<div style="font-size:12px;color:var(--muted);margin-top:4px;padding-left:26px;line-height:1.5;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden">${esc(d.description)}</div>
              <button data-action="preview-detail" data-program="${p.id}" style="margin-left:26px;margin-top:2px;color:var(--gold);background:none;border:none;cursor:pointer;font-size:11px;padding:0;font-family:inherit">More &rsaquo;</button>` : ''}
              ${d.note ? `<div style="font-size:11px;color:var(--gold-light);font-style:italic;margin-top:4px;padding-left:26px">${esc(d.note)}</div>` : ''}
            </div>`;
          }).join('')}
        </div>`;
      }).join('') || '<p class="empty">No Tap or Voice offerings are available for the sample performers right now.</p>'}
      <button class="btn" style="margin-top:14px" data-action="preview-submit" ${anySelected ? '' : 'disabled'}>Register (preview only)</button>
    </div>`;
};

VIEW_INIT['a-classes-preview'] = () => {
  document.querySelectorAll('[data-action="preview-toggle"]').forEach(cb => {
    cb.addEventListener('change', () => classesPreviewToggle(cb.dataset.kid, cb.dataset.program));
  });
  document.querySelectorAll('[data-action="preview-submit"]').forEach(btn => {
    btn.addEventListener('click', classesPreviewSubmit);
  });
  document.querySelectorAll('[data-action="preview-detail"]').forEach(btn => {
    btn.addEventListener('click', () => previewDetail(btn.dataset.program));
  });
};
// Mirrors parent-classes.js's tvDetail() so the preview shows exactly what
// a parent would see if they clicked "More".
function previewDetail(programId) {
  const p = PROGRAMS.find(x => x.id === programId);
  if (!p) return;
  const d = p.details || {};
  const metaParts = [d.dates, d.time, d.teacher].filter(Boolean);
  openModal(`
    <h3>${esc(p.name)}</h3>
    ${metaParts.length ? `<p style="font-size:13px;color:var(--muted);margin-bottom:12px">${metaParts.map(esc).join(' · ')}</p>` : ''}
    ${d.description ? `<p style="font-size:14px;color:var(--cream);line-height:1.6;margin-bottom:${d.note ? '10px' : '0'}">${esc(d.description)}</p>` : ''}
    ${d.note ? `<p style="font-size:12px;color:var(--gold-light);font-style:italic;margin:0">${esc(d.note)}</p>` : ''}
  `);
}
